import { AfterViewInit, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { APP_VERSION, AttachmentType, ClockService, Complaint, ComplaintNote, ComplaintPosition, DocumentAttachment } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ComplaintService } from '../complaint.service';
import { ConfigService, core, TranslateService } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';

@Component({
    selector: 'icc-complaint-note-dialog',
    templateUrl: './complaint-note-dialog.component.html',
    styleUrls: ['./complaint-note-dialog.component.scss'],
})
export class ComplaintNoteDialogComponent implements AfterViewInit {
    showFullPositionTitles: boolean;

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint,
            complaintNote: ComplaintNote,
            complaintsPositionsMap: Map<string, ComplaintPosition>,
            attachments: DocumentAttachment[],
            isReply: boolean,
        },
        @Inject(APP_VERSION) private appVersion: string,
        private complaintService: ComplaintService,
        private cdr: ChangeDetectorRef,
        private configService: ConfigService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {
        this.showFullPositionTitles = this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
        this.authorizedRetailerDistributorComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId !== null;
        this.distributorManufacturerComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId === null;
    }

    getPositionTitle(position: ComplaintPosition) {
        return this.complaintService.getPositionTitle(position, this.showFullPositionTitles);
    }

    ngAfterViewInit() {
        this.cdr.detectChanges();
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    async onAddAttachment(files: FileList, type: DocumentAttachment['attachmentType']) {
        const maxSizeInMB = 7;
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        const maxFiles = 5;

        if (files[0]) {
            if (files[0].size > maxSizeInBytes) {
                this.toastService.add({
                    severity: 'error',
                    title: this.translateService.instant('COMPLAINT|Plik jest zbyt duży.'),
                    body: this.translateService.instant('COMPLAINT|Maksymalna wielkość to {maxSizeInMB}MB.', { maxSizeInMB }),
                });
                return;
            }

            if (type === 'complaintNoteAttachment' && this.data.attachments.length >= maxFiles) {
                this.toastService.add({
                    severity: 'warn',
                    title: this.translateService.instant('COMPLAINT|Osiągnięto limit zdjęć.'),
                    body: this.translateService.instant('COMPLAINT|Możesz dodać maksymalnie {maxFiles} zdjęć.', { maxFiles }),
                });
                return;
            }

            const data = await core.blobToArrayBuffer(files[0]);
            const newAttachment: DocumentAttachment = {
                uuid: core.generateUUID(),
                type: 'complaint',
                documentId: this.data.complaint?.uuid,
                complaintNoteId: this.data.complaintNote?.uuid,
                fileType: files[0].type,
                created: new Date(),
                modified: new Date(),
                machineId: ClockService.getClock().timestamp.node(),
                name: files[0].name,
                data,
                attachmentType: type,
                description: '',
            };
            await this.complaintService.cacheAttachment(newAttachment.uuid, files[0]);
            this.data.attachments.push(newAttachment);
        }
    }

    removeAttachment(type: DocumentAttachment['attachmentType'], uuid: string) {
        const attachment = this.data.attachments.find((el) => el.attachmentType === type && el.uuid === uuid);
        if (attachment) {
            (document.getElementById(type + this.data.complaintNote.uuid) as HTMLInputElement).value = '';
            this.data.attachments.splice(this.data.attachments.findIndex(
                (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
            ), 1);
        }
    }

    getAttachments(type: AttachmentType): DocumentAttachment[] {
        return this.data.attachments.filter(
            (attachment) => attachment.attachmentType === type
        );
    }

    getAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = this.data.attachments.find(
            (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
        );
        return attachment;
    }

    async save() {
        this.dialogRef.close({
            complaintNote: this.data.complaintNote,
            attachments: this.data.attachments,
        });
    }

    close() {
        this.dialogRef.close();
    }
}
